import type { ReactElement, ReactNode } from "react";
import { color, radius, space } from "@tokens/index";
import { Button, Text } from "@primitives/index";

/**
 * Empty and error states for admin content, per charter §8. The loading
 * state is the `SkeletonRows` primitive with `shape="card"`.
 */

interface EmptyLineProps {
    readonly children: ReactNode;
    readonly action?: ReactNode;
}

export function EmptyLine({ children, action }: EmptyLineProps): ReactElement {
    return (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: space[3],
            }}
        >
            <Text as="p" role="body" tone="muted" style={{ minWidth: 0 }}>
                {children}
            </Text>
            {action}
        </div>
    );
}

interface ErrorAlertProps {
    readonly children: ReactNode;
    readonly onRetry?: () => void;
}

export function ErrorAlert({ children, onRetry }: ErrorAlertProps): ReactElement {
    return (
        <div
            role="alert"
            style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: space[3],
                paddingInline: space[4],
                paddingBlock: space[3],
                borderRadius: radius.md,
                background: color.status.dangerBg,
            }}
        >
            <Text role="bodySm" tone="danger" style={{ minWidth: 0, flex: 1 }}>
                {children}
            </Text>
            {onRetry !== undefined && (
                <Button variant="ghost" size="sm" onClick={onRetry}>
                    Retry
                </Button>
            )}
        </div>
    );
}

/** A mutation error line. Renders nothing when `error` is null. */
export function MutationError({ error }: { readonly error: Error | null }): ReactElement | null {
    if (error === null) return null;
    return <ErrorAlert>{error.message}</ErrorAlert>;
}
